import axios, { AxiosError } from 'axios';
import { BackendError } from '../types';
import * as DisplayStrings from '../displayStrings';

/**
 * True when the error is an HTTP 404. Used to treat a missing entity as a terminal outcome:
 * skip react-query's retries and render a not-found page instead of a generic error toast.
 */
export const isNotFoundError = (error: unknown): boolean => axios.isAxiosError(error) && error.response?.status === 404;

/**
 * The QRI of an event the backend says the user can only read in another company,
 * or undefined for any other error. The switch instance page takes it from there.
 */
export const wrongInstanceQri = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) {
    return undefined;
  }
  const data = error.response.data as { slug?: string; qri?: string } | undefined;
  return data?.slug === 'raise-access-in-wrong-instance' ? data.qri : undefined;
};

const extractMessageFromErrorArray = (errors: BackendError[]): string => {
  return errors && errors.length ? errors[0]!.description : DisplayStrings.DefaultErrorMessage;
};

export const extractMessageFromError = (e: AxiosError<any, any>): string => {
  // This is intended to work for a variety of errors, from medtech, fastapi validation, and custom
  if (e.response == null) {
    return DisplayStrings.DefaultErrorMessage;
  }
  const errorMessage = e.response.data.detail ?? e.response.data.message ?? e.response.data;
  if (typeof errorMessage === 'string') {
    return errorMessage;
  } else if (typeof errorMessage === 'object' && !Array.isArray(errorMessage)) {
    return extractMessageFromErrorArray(errorMessage.errors);
  }
  // This gets fastAPI errors
  const errors = e.response.data.errors;
  return extractMessageFromErrorArray(errors);
};
